/* =====================================================================
 * Layer 3 — App-level flat colour map (the shape styled-components sees).
 *
 * Resolves a Palette (layer 1) through the semantic mapping into the
 * exact key shape the legacy askyourai lightTheme / darkTheme exposed
 * as `colors.*`. Any consumer of `theme.colors.bgPrimary`,
 * `theme.colors.textSecondary`, etc. keeps working unchanged.
 *
 * Adding a new slot here requires adding it to every consuming theme
 * via `createTheme` (hence the single source here).
 * ===================================================================== */

import type { Palette } from '../tokens/palette';

export interface AppColors {
  bgPrimary: string;
  bgSecondary: string;
  bgTertiary: string;
  bgSidebar: string;
  bgInput: string;
  bgInputHover: string;
  bgHover: string;
  bgCode: string;
  bgCodeHeader: string;
  bgUserMessage: string;
  bgAssistantMessage: string;
  bgTooltip: string;
  bgOverlay: string;

  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textInverse: string;
  textLink: string;

  border: string;
  borderLight: string;
  borderInput: string;

  accent: string;
  accentHover: string;
  accentLight: string;

  /** Vivid brand point colour (page highlight, CTA, selection glow). */
  brand: string;
  brandHover: string;
  brandSoft: string;
  brandGradientEnd: string;
  /** Text/icon colour on a `brand` fill — white in both modes (see palette). */
  brandContrastText: string;

  success: string;
  error: string;
  errorBg: string;
  warning: string;
  warningBg: string;
  info: string;
  infoBg: string;

  /** Usage bar (quota widget) */
  usageBarHealthy: string;
  usageBarCaution: string;
  usageBarCritical: string;
  usageBarNeutral: string;

  scrollbar: string;
  scrollbarHover: string;
}

/**
 * Flatten a Palette into the `colors.*` shape expected by legacy
 * styled-components consumers.
 */
export function resolveColors(palette: Palette): AppColors {
  return {
    bgPrimary: palette.surface.canvas,
    bgSecondary: palette.surface.raised,
    bgTertiary: palette.surface.sunken,
    bgSidebar: palette.surface.sidebar,
    bgInput: palette.surface.input,
    bgInputHover: palette.surface.inputHover,
    bgHover: palette.surface.hover,
    bgCode: palette.surface.code,
    bgCodeHeader: palette.surface.codeHeader,
    bgUserMessage: palette.surface.userMessage,
    bgAssistantMessage: palette.surface.assistantMessage,
    bgTooltip: palette.surface.tooltip,
    bgOverlay: palette.surface.overlay,

    textPrimary: palette.text.primary,
    textSecondary: palette.text.secondary,
    textMuted: palette.text.muted,
    textInverse: palette.text.inverse,
    textLink: palette.text.link,

    border: palette.border.default,
    borderLight: palette.border.light,
    borderInput: palette.border.input,

    accent: palette.accent.base,
    accentHover: palette.accent.hover,
    accentLight: palette.accent.light,

    brand: palette.brand.base,
    brandHover: palette.brand.hover,
    brandSoft: palette.brand.softBg,
    brandGradientEnd: palette.brand.gradientEnd,
    brandContrastText: palette.brand.contrastText,

    success: palette.feedback.success,
    error: palette.feedback.error,
    errorBg: palette.feedback.errorBg,
    warning: palette.feedback.warning,
    warningBg: palette.feedback.warningBg,
    info: palette.feedback.info,
    infoBg: palette.feedback.infoBg,

    usageBarHealthy: palette.usage.healthy,
    usageBarCaution: palette.usage.caution,
    usageBarCritical: palette.usage.critical,
    usageBarNeutral: palette.usage.neutral,

    scrollbar: palette.scrollbar.thumb,
    scrollbarHover: palette.scrollbar.thumbHover,
  };
}
